"use client"

// src/CostsComponent.tsx
import { createContext, useContext } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// src/components/costs/CostsComponent.tsx
import { useState as useState2 } from "react";

// src/components/costs/CostsTable.tsx
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@umd-dit-ai-workspace/shared-ui";
import { Badge } from "@umd-dit-ai-workspace/shared-ui";
import {
  Loader2,
  CircleDollarSign,
  ZapIcon
} from "lucide-react";
import { jsx, jsxs } from "react/jsx-runtime";
function CostsTable({ costs, isLoading, year }) {
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const formatUSD = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(parseFloat(amount));
  };
  const getDisplayName = (costType) => {
    const rollups = costs[costType];
    if (rollups && Array.isArray(rollups) && rollups.length > 0) {
      const firstRollup = rollups[0];
      if (firstRollup?.cost_display_name) {
        return firstRollup.cost_display_name;
      }
    }
    return costType.replace(/_/g, " ").replace(/([A-Z])/g, " $1").trim();
  };
  const getMonthTotal = (costType, month) => {
    if (!costs[costType]) return "$0.00";
    if (!Array.isArray(costs[costType])) return "$0.00";
    const monthRollup = costs[costType].find((r) => r?.month === month);
    return monthRollup ? formatUSD(monthRollup.total_usd) : "$0.00";
  };
  const getMonthlyTotal = (month) => {
    const total = Object.keys(costs).reduce((sum, type) => {
      if (!costs[type] || !Array.isArray(costs[type])) return sum;
      const monthRollup = costs[type].find((r) => r?.month === month);
      return sum + (monthRollup ? parseFloat(monthRollup.total_usd) : 0);
    }, 0);
    return formatUSD(total.toString());
  };
  const getYearlyTotal = (costType) => {
    if (!costs[costType]) return "$0.00";
    if (!Array.isArray(costs[costType])) return "$0.00";
    const total = costs[costType].reduce(
      (sum, rollup) => sum + (rollup?.total_usd ? parseFloat(rollup.total_usd) : 0),
      0
    );
    return formatUSD(total.toString());
  };
  const getOverallTotal = () => {
    const total = Object.keys(costs).reduce((sum, type) => {
      if (!costs[type] || !Array.isArray(costs[type])) return sum;
      const typeTotal = costs[type].reduce(
        (subSum, rollup) => subSum + (rollup?.total_usd ? parseFloat(rollup.total_usd) : 0),
        0
      );
      return sum + typeTotal;
    }, 0);
    return formatUSD(total.toString());
  };
  const getMonthName = (month) => {
    return new Date(2024, month - 1).toLocaleString("default", { month: "short" });
  };
  const isEmpty = Object.keys(costs).length === 0;
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: "border rounded-md p-8 bg-background/50", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-center gap-2", children: [
      /* @__PURE__ */ jsx(Loader2, { className: "h-4 w-4 animate-spin" }),
      /* @__PURE__ */ jsx("span", { className: "text-sm text-muted-foreground", children: "Loading costs..." })
    ] }) });
  }
  if (isEmpty) {
    return /* @__PURE__ */ jsx("div", { className: "border rounded-md p-8 text-center", children: /* @__PURE__ */ jsxs("p", { className: "text-muted-foreground", children: [
      "No cost data available for ",
      year
    ] }) });
  }
  return /* @__PURE__ */ jsxs("div", { className: "rounded-md border space-y-4", children: [
    /* @__PURE__ */ jsxs(Table, { children: [
      /* @__PURE__ */ jsx(TableHeader, { children: /* @__PURE__ */ jsxs(TableRow, { className: "hover:bg-muted/5", children: [
        /* @__PURE__ */ jsx(TableHead, { children: "Service" }),
        months.map((month) => /* @__PURE__ */ jsx(TableHead, { className: "text-right", children: getMonthName(month) }, month)),
        /* @__PURE__ */ jsx(TableHead, { className: "text-right bg-muted/50", children: "Total" })
      ] }) }),
      /* @__PURE__ */ jsxs(TableBody, { children: [
        Object.keys(costs).map((costType) => {
          const displayName = getDisplayName(costType);
          return /* @__PURE__ */ jsxs(TableRow, { className: "group hover:bg-muted/5", children: [
            /* @__PURE__ */ jsx(TableCell, { children: /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2", children: /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "gap-1 px-2 py-0.5", children: [
              /* @__PURE__ */ jsx(ZapIcon, { className: "h-3.5 w-3.5" }),
              /* @__PURE__ */ jsx("span", { children: displayName })
            ] }) }) }),
            months.map((month) => /* @__PURE__ */ jsx(TableCell, { className: "text-right tabular-nums", children: getMonthTotal(costType, month) }, month)),
            /* @__PURE__ */ jsx(TableCell, { className: "text-right bg-muted/50 font-medium tabular-nums", children: getYearlyTotal(costType) })
          ] }, costType);
        }),
        /* @__PURE__ */ jsxs(TableRow, { className: "border-t-2 hover:bg-muted/5", children: [
          /* @__PURE__ */ jsx(TableCell, { children: /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2", children: /* @__PURE__ */ jsxs(Badge, { className: "gap-1 px-2 py-0.5", children: [
            /* @__PURE__ */ jsx(CircleDollarSign, { className: "h-3.5 w-3.5" }),
            /* @__PURE__ */ jsx("span", { className: "font-bold", children: "Total" })
          ] }) }) }),
          months.map((month) => /* @__PURE__ */ jsx(TableCell, { className: "text-right font-bold tabular-nums", children: getMonthlyTotal(month) }, month)),
          /* @__PURE__ */ jsx(TableCell, { className: "text-right bg-muted font-bold tabular-nums", children: getOverallTotal() })
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "p-4 border-t bg-muted/10", children: /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2", children: /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-muted-foreground", children: [
      "Last Updated: ",
      (/* @__PURE__ */ new Date()).toLocaleDateString()
    ] }) }) })
  ] });
}

// src/components/costs/YearSelect.tsx
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@umd-dit-ai-workspace/shared-ui";
import { Badge as Badge2 } from "@umd-dit-ai-workspace/shared-ui";
import { CalendarIcon } from "lucide-react";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
function YearSelect({ value, onChange }) {
  const currentYear = (/* @__PURE__ */ new Date()).getFullYear();
  const years = [currentYear - 1, currentYear];
  return /* @__PURE__ */ jsxs2("div", { className: "flex items-center gap-2", children: [
    /* @__PURE__ */ jsxs2(Badge2, { variant: "outline", className: "gap-1", children: [
      /* @__PURE__ */ jsx2(CalendarIcon, { className: "h-3 w-3" }),
      /* @__PURE__ */ jsx2("span", { children: "Year" })
    ] }),
    /* @__PURE__ */ jsxs2(
      Select,
      {
        value: value.toString(),
        onValueChange: (v) => onChange(parseInt(v)),
        children: [
          /* @__PURE__ */ jsx2(SelectTrigger, { className: "w-[120px]", children: /* @__PURE__ */ jsx2(SelectValue, { placeholder: "Select year" }) }),
          /* @__PURE__ */ jsx2(SelectContent, { children: years.map((year) => /* @__PURE__ */ jsx2(SelectItem, { value: year.toString(), children: year }, year)) })
        ]
      }
    )
  ] });
}

// src/hooks/useCosts.ts
import { useQuery } from "@tanstack/react-query";

// src/api/costs-api.ts
var createCostsApi = (api_instance) => ({
  getYearlyCosts: async (app_id, year, organization_id = "demo-org", workspace_id = "demo-workspace", level = "app") => {
    console.log("\u{1F50D} Fetching yearly costs for app:", app_id, "year:", year, "org:", organization_id, "workspace:", workspace_id, "level:", level);
    let endpoint = "";
    switch (level) {
      case "app":
        endpoint = `/costs/rollups/${year}`;
        break;
      case "org":
        endpoint = `/org/${organization_id}/costs/rollups/${year}`;
        break;
      case "workspace":
        endpoint = `/org/${organization_id}/ws/${workspace_id}/costs/rollups/${year}`;
        break;
      default:
        endpoint = `/org/${organization_id}/ws/${workspace_id}/costs/rollups/${year}`;
    }
    const response = await api_instance.get(endpoint);
    console.log("\u2705 Fetched yearly costs:", response.data);
    return response.data;
  }
});

// src/hooks/useCosts.ts
var getCurrentYear = () => (/* @__PURE__ */ new Date()).getFullYear();
var useCosts = (year = getCurrentYear(), app_id = "default-app", selectedOrganizationId = "demo-org", selectedWorkspaceId = "demo-workspace", level = "app") => {
  const { apiInstance } = useApi();
  const costsApi = createCostsApi(apiInstance);
  const { data, isPending, isError } = useQuery({
    queryKey: ["costs", app_id, year, selectedOrganizationId, selectedWorkspaceId, level],
    queryFn: async () => {
      console.log("[useCosts] Fetching costs for year:", year, "level:", level);
      if (!selectedOrganizationId || level !== "org" && !selectedWorkspaceId) {
        throw new Error("Required organization or workspace not selected");
      }
      try {
        const costs = await costsApi.getYearlyCosts(
          app_id,
          year,
          selectedOrganizationId,
          selectedWorkspaceId,
          level
        );
        return costs;
      } catch (error) {
        console.error("Failed to load costs:", error);
        throw error;
      }
    },
    staleTime: 3e5,
    // 5 minutes
    gcTime: 36e5,
    // 1 hour
    enabled: !!selectedOrganizationId && (level === "org" || !!selectedWorkspaceId)
  });
  return {
    costs: data || {},
    isLoading: isPending,
    error: isError ? "Failed to load cost data" : null
  };
};

// src/components/costs/CostsComponent.tsx
import { Alert as Alert2, AlertDescription as AlertDescription2 } from "@umd-dit-ai-workspace/shared-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@umd-dit-ai-workspace/shared-ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@umd-dit-ai-workspace/shared-ui";

// src/components/ui/AuthErrorAlert.tsx
import { useState, useEffect } from "react";
import { Alert, AlertDescription } from "@umd-dit-ai-workspace/shared-ui";
import { AlertTriangle } from "lucide-react";

// src/api/api.ts
var authEvents = {
  listeners: /* @__PURE__ */ new Set(),
  onAuthError(callback) {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  },
  emitAuthError(event) {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        console.error("Error in auth error listener:", error);
      }
    });
  }
};

// src/components/ui/AuthErrorAlert.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
function AuthErrorAlert() {
  const [authError, setAuthError] = useState(null);
  useEffect(() => {
    const unsubscribe = authEvents.onAuthError((event) => {
      setAuthError(event.message || "Authentication error occurred");
      setTimeout(() => {
        setAuthError(null);
      }, 5e3);
    });
    return () => {
      unsubscribe();
    };
  }, []);
  if (!authError) return null;
  return /* @__PURE__ */ jsxs3(Alert, { variant: "destructive", className: "mb-4", children: [
    /* @__PURE__ */ jsx3(AlertTriangle, { className: "h-4 w-4" }),
    /* @__PURE__ */ jsx3(AlertDescription, { children: authError })
  ] });
}

// src/components/costs/CostsComponent.tsx
import {
  AlertTriangle as AlertTriangle2,
  DollarSign,
  TrendingUp,
  CalendarDays,
  ArrowUpRight,
  ArrowDownRight,
  CalendarRange,
  LayoutDashboard,
  Building,
  FolderGit2
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
function TrendIcon({ value }) {
  return value >= 0 ? /* @__PURE__ */ jsx4(ArrowUpRight, { className: "h-4 w-4 text-red-500" }) : /* @__PURE__ */ jsx4(ArrowDownRight, { className: "h-4 w-4 text-green-500" });
}
function CostsComponent({
  organization_id,
  workspace_id,
  app_id,
  defaultCostView,
  isAdmin
}) {
  const [selectedYear, setSelectedYear] = useState2((/* @__PURE__ */ new Date()).getFullYear());
  const getDefaultTab = () => {
    if (!isAdmin && (defaultCostView === "app" || defaultCostView === "org")) {
      return "workspace";
    }
    return defaultCostView;
  };
  const [activeTab, setActiveTab] = useState2(getDefaultTab());
  const handleTabChange = (value) => {
    if (!isAdmin && (value === "app" || value === "org")) {
      return;
    }
    setActiveTab(value);
  };
  const { costs, isLoading, error } = useCosts(
    selectedYear,
    app_id,
    organization_id,
    workspace_id,
    activeTab
  );
  const getTotalCost = () => {
    if (!costs || Object.keys(costs).length === 0) {
      return 0;
    }
    return Object.values(costs).reduce((total, serviceCosts) => {
      if (!Array.isArray(serviceCosts)) return total;
      return total + serviceCosts.reduce(
        (serviceTotal, cost) => serviceTotal + parseFloat(cost.total_usd),
        0
      );
    }, 0);
  };
  const getMostExpensiveMonth = () => {
    if (!costs || Object.keys(costs).length === 0) {
      return {
        month: (/* @__PURE__ */ new Date()).toLocaleString("default", { month: "long" }),
        total: 0
      };
    }
    const monthTotals = Array.from({ length: 12 }, (_, month) => {
      const total = Object.values(costs).reduce((sum, serviceCosts) => {
        if (!Array.isArray(serviceCosts)) return sum;
        const monthCost = serviceCosts.find((cost) => cost.month === month + 1);
        return sum + (monthCost ? parseFloat(monthCost.total_usd) : 0);
      }, 0);
      return { month: month + 1, total };
    });
    if (monthTotals.length === 0 || monthTotals.every((m) => m.total === 0)) {
      return {
        month: (/* @__PURE__ */ new Date()).toLocaleString("default", { month: "long" }),
        total: 0
      };
    }
    const maxMonth = monthTotals.reduce(
      (max, current2) => current2.total > max.total ? current2 : max,
      monthTotals[0]
    );
    return {
      month: new Date(2024, maxMonth.month - 1).toLocaleString("default", { month: "long" }),
      total: maxMonth.total
    };
  };
  const getTrendData = () => {
    if (!costs || Object.keys(costs).length === 0) {
      return Array.from({ length: 12 }, (_, month) => ({
        name: new Date(2024, month).toLocaleString("default", { month: "short" }),
        total: 0
      }));
    }
    return Array.from({ length: 12 }, (_, month) => {
      const monthTotal = Object.values(costs).reduce((sum, serviceCosts) => {
        if (!Array.isArray(serviceCosts)) return sum;
        const monthCost = serviceCosts.find((cost) => cost.month === month + 1);
        return sum + (monthCost ? parseFloat(monthCost.total_usd) : 0);
      }, 0);
      return {
        name: new Date(2024, month).toLocaleString("default", { month: "short" }),
        total: monthTotal
      };
    });
  };
  const getPreviousMonthComparison = () => {
    if (!costs || Object.keys(costs).length === 0) {
      return {
        current: 0,
        previous: 0,
        change: 0
      };
    }
    const currentMonth = (/* @__PURE__ */ new Date()).getMonth() + 1;
    const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const currentTotal = Object.values(costs).reduce((sum, serviceCosts) => {
      if (!Array.isArray(serviceCosts)) return sum;
      const monthCost = serviceCosts.find((cost) => cost.month === currentMonth);
      return sum + (monthCost ? parseFloat(monthCost.total_usd) : 0);
    }, 0);
    const previousTotal = Object.values(costs).reduce((sum, serviceCosts) => {
      if (!Array.isArray(serviceCosts)) return sum;
      const monthCost = serviceCosts.find((cost) => cost.month === previousMonth);
      return sum + (monthCost ? parseFloat(monthCost.total_usd) : 0);
    }, 0);
    let percentChange = 0;
    if (previousTotal === 0) {
      percentChange = currentTotal > 0 ? 100 : 0;
    } else {
      percentChange = (currentTotal - previousTotal) / previousTotal * 100;
    }
    return {
      current: currentTotal,
      previous: previousTotal,
      change: percentChange
    };
  };
  const formatUSD = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };
  const totalCost = getTotalCost();
  const { month: expensiveMonth, total: expensiveTotal } = getMostExpensiveMonth();
  const { current, change } = getPreviousMonthComparison();
  const trendData = getTrendData();
  const getTabTitle = () => {
    switch (activeTab) {
      case "app":
        return `Global App Costs - ${app_id}`;
      case "org":
        return `Organization Costs - ${organization_id}`;
      case "workspace":
        return `Workspace Costs - ${organization_id}/${workspace_id}`;
      default:
        return "Costs";
    }
  };
  const renderSummaryCards = () => /* @__PURE__ */ jsxs4("div", { className: "grid gap-4 md:grid-cols-2 lg:grid-cols-4", children: [
    /* @__PURE__ */ jsxs4(Card, { children: [
      /* @__PURE__ */ jsxs4(CardHeader, { className: "flex flex-row items-center justify-between space-y-0 pb-2", children: [
        /* @__PURE__ */ jsx4(CardTitle, { className: "text-sm font-medium", children: "Total Costs" }),
        /* @__PURE__ */ jsx4(DollarSign, { className: "h-4 w-4 text-muted-foreground" })
      ] }),
      /* @__PURE__ */ jsxs4(CardContent, { children: [
        /* @__PURE__ */ jsx4("div", { className: "text-2xl font-bold", children: formatUSD(totalCost) }),
        /* @__PURE__ */ jsxs4("p", { className: "text-xs text-muted-foreground", children: [
          "For year ",
          selectedYear
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsxs4(Card, { children: [
      /* @__PURE__ */ jsxs4(CardHeader, { className: "flex flex-row items-center justify-between space-y-0 pb-2", children: [
        /* @__PURE__ */ jsx4(CardTitle, { className: "text-sm font-medium", children: "Most Expensive Month" }),
        /* @__PURE__ */ jsx4(TrendingUp, { className: "h-4 w-4 text-muted-foreground" })
      ] }),
      /* @__PURE__ */ jsxs4(CardContent, { children: [
        /* @__PURE__ */ jsx4("div", { className: "text-2xl font-bold", children: formatUSD(expensiveTotal) }),
        /* @__PURE__ */ jsx4("p", { className: "text-xs text-muted-foreground", children: expensiveMonth })
      ] })
    ] }),
    /* @__PURE__ */ jsxs4(Card, { children: [
      /* @__PURE__ */ jsxs4(CardHeader, { className: "flex flex-row items-center justify-between space-y-0 pb-2", children: [
        /* @__PURE__ */ jsx4(CardTitle, { className: "text-sm font-medium", children: "Current Month" }),
        /* @__PURE__ */ jsx4(CalendarDays, { className: "h-4 w-4 text-muted-foreground" })
      ] }),
      /* @__PURE__ */ jsxs4(CardContent, { children: [
        /* @__PURE__ */ jsx4("div", { className: "text-2xl font-bold", children: formatUSD(current) }),
        /* @__PURE__ */ jsxs4("div", { className: "flex items-center text-xs", children: [
          /* @__PURE__ */ jsx4(TrendIcon, { value: change }),
          /* @__PURE__ */ jsxs4("span", { className: change >= 0 ? "text-red-500" : "text-green-500", children: [
            Math.abs(change).toFixed(1),
            "% from last month"
          ] })
        ] })
      ] })
    ] }),
    /* @__PURE__ */ jsxs4(Card, { children: [
      /* @__PURE__ */ jsxs4(CardHeader, { className: "flex flex-row items-center justify-between space-y-0 pb-2", children: [
        /* @__PURE__ */ jsx4(CardTitle, { className: "text-sm font-medium", children: "Monthly Trend" }),
        /* @__PURE__ */ jsx4(CalendarRange, { className: "h-4 w-4 text-muted-foreground" })
      ] }),
      /* @__PURE__ */ jsx4(CardContent, { children: /* @__PURE__ */ jsx4("div", { className: "h-[80px]", children: /* @__PURE__ */ jsx4(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs4(LineChart, { data: trendData, children: [
        /* @__PURE__ */ jsx4(
          Line,
          {
            type: "monotone",
            dataKey: "total",
            stroke: "#2563eb",
            strokeWidth: 2,
            dot: false
          }
        ),
        /* @__PURE__ */ jsx4(XAxis, { dataKey: "name", hide: true }),
        /* @__PURE__ */ jsx4(YAxis, { hide: true }),
        /* @__PURE__ */ jsx4(
          Tooltip,
          {
            formatter: (value) => [formatUSD(value), "Total"],
            labelFormatter: (label) => `${label}`
          }
        )
      ] }) }) }) })
    ] })
  ] });
  const renderTabContent = () => /* @__PURE__ */ jsxs4("div", { children: [
    /* @__PURE__ */ jsx4("h2", { className: "text-lg font-medium mb-4", children: getTabTitle() }),
    /* @__PURE__ */ jsx4(AuthErrorAlert, {}),
    error && /* @__PURE__ */ jsxs4(Alert2, { variant: "destructive", children: [
      /* @__PURE__ */ jsx4(AlertTriangle2, { className: "h-4 w-4" }),
      /* @__PURE__ */ jsx4(AlertDescription2, { children: error })
    ] }),
    renderSummaryCards(),
    /* @__PURE__ */ jsx4(
      CostsTable,
      {
        costs,
        isLoading,
        year: selectedYear
      }
    )
  ] });
  return /* @__PURE__ */ jsx4("div", { className: "container mx-auto px-4", children: /* @__PURE__ */ jsxs4("div", { className: "space-y-6 bg-background text-foreground p-4 rounded-lg", children: [
    /* @__PURE__ */ jsxs4("div", { className: "flex justify-between items-center", children: [
      /* @__PURE__ */ jsxs4("div", { children: [
        /* @__PURE__ */ jsx4("h1", { className: "text-2xl font-bold", children: "Cost Tracking" }),
        /* @__PURE__ */ jsx4("p", { className: "text-sm text-muted-foreground mt-1", children: "Track and monitor AI service usage costs" })
      ] }),
      /* @__PURE__ */ jsx4(YearSelect, { value: selectedYear, onChange: setSelectedYear })
    ] }),
    /* @__PURE__ */ jsxs4("div", { className: "border rounded-md p-4 bg-background", children: [
      /* @__PURE__ */ jsx4("h3", { className: "text-md font-medium mb-2", children: "Cost View Level" }),
      /* @__PURE__ */ jsxs4(
        Tabs,
        {
          defaultValue: defaultCostView,
          value: activeTab,
          onValueChange: handleTabChange,
          className: "space-y-4",
          children: [
            /* @__PURE__ */ jsxs4(TabsList, { className: `grid ${isAdmin ? "grid-cols-3" : "grid-cols-1"} w-full max-w-lg mx-auto bg-muted`, children: [
              isAdmin && /* @__PURE__ */ jsxs4(TabsTrigger, { value: "app", className: "flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm", children: [
                /* @__PURE__ */ jsx4(LayoutDashboard, { className: "h-4 w-4" }),
                /* @__PURE__ */ jsx4("span", { children: "Global App" })
              ] }),
              isAdmin && /* @__PURE__ */ jsxs4(TabsTrigger, { value: "org", className: "flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm", children: [
                /* @__PURE__ */ jsx4(Building, { className: "h-4 w-4" }),
                /* @__PURE__ */ jsx4("span", { children: "Organization" })
              ] }),
              /* @__PURE__ */ jsxs4(TabsTrigger, { value: "workspace", className: "flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm", children: [
                /* @__PURE__ */ jsx4(FolderGit2, { className: "h-4 w-4" }),
                /* @__PURE__ */ jsx4("span", { children: "Workspace" })
              ] })
            ] }),
            isAdmin && /* @__PURE__ */ jsx4(TabsContent, { value: "app", children: renderTabContent() }),
            isAdmin && /* @__PURE__ */ jsx4(TabsContent, { value: "org", children: renderTabContent() }),
            /* @__PURE__ */ jsx4(TabsContent, { value: "workspace", children: renderTabContent() })
          ]
        }
      )
    ] })
  ] }) });
}

// src/CostsComponent.tsx
import { Fragment, jsx as jsx5 } from "react/jsx-runtime";
var queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});
var ApiContext = createContext(null);
var useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return context;
};
var AuthErrorHandler = ({ children }) => {
  return /* @__PURE__ */ jsx5(Fragment, { children });
};
var CostsComponent2 = ({
  app_id,
  api_instance,
  organization_id,
  workspace_id,
  default_cost_view,
  is_admin
}) => {
  console.log(`[CostsComponent] Using provided API instance for app: ${app_id}`);
  return /* @__PURE__ */ jsx5(QueryClientProvider, { client: queryClient, children: /* @__PURE__ */ jsx5(ApiContext.Provider, { value: { apiInstance: api_instance }, children: /* @__PURE__ */ jsx5(AuthErrorHandler, { children: /* @__PURE__ */ jsx5(
    CostsComponent,
    {
      organization_id,
      workspace_id,
      app_id,
      defaultCostView: default_cost_view,
      isAdmin: is_admin
    }
  ) }) }) });
};

// src/index.ts
var configureCostsAuth = () => {
  console.warn("configureCostsAuth is deprecated and no longer needed");
};
export {
  CostsComponent2 as CostsComponent,
  authEvents,
  configureCostsAuth,
  useApi
};
//# sourceMappingURL=index.js.map